import { formatMoney } from '../money';
import type { CalculatedShippingSettings, ShippingServiceKey } from '../settings';
import {
  UF_INFO,
  capitalOf,
  haversineKm,
  isCapitalCity,
  isSameCity,
  isValidLatLng,
  roundKm,
  toUF,
  type GeoPrecision,
  type LatLng,
  type PlaceRef,
  type UF,
} from './geo';

/**
 * Frete no método de cálculo dos Correios (PAC e SEDEX), sem rede.
 *
 * Como os Correios calculam (fontes oficiais, consultadas em 14/10/2026):
 * 1. Peso cobrado = maior entre o peso real e o peso cúbico (C × L × A ÷ 6.000, em cm), mas encomenda com
 *    peso cúbico de até 5 kg é sempre cobrada pelo peso real. Termo de Condições de Prestação de Serviços de
 *    Encomendas Nacionais, itens 6.1 a 6.3 (PDF oficial em correios.com.br, rodapé de 08/04/2026).
 * 2. Limites do pacote: até 30 kg; maior lado até 100 cm; soma dos 3 lados até 200 cm
 *    (correios.com.br/enviar/encomendas/nacional).
 * 3. O preço sai de uma tabela "faixa de peso × trecho". Faixas: 0 a 300 g, 301 a 500 g, 501 g a 1 kg e depois
 *    de 1 em 1 kg até 10 kg; acima disso, "kg adicional". Trechos (colunas) L1 a L4 (local), E1 a E4 (estadual e
 *    divisa), N1 a N4 (entre capitais e cidades A+), P1 a P4 (capitais e cidades B, "interior prioritário") e
 *    I1 a I4 (demais interestaduais). O PAC não tem colunas L. Tabelas oficiais Correios Log+ de 03/04/2024.
 * 4. Valor declarado (sem contrato): 2% sobre o valor que passar da indenização automática (R$ 25,63 no PAC e no
 *    SEDEX), limitado a R$ 4.668,29 (PAC) e R$ 39.680,48 (SEDEX), vigente desde 12/04/2026
 *    (correios.com.br/enviar/servicos-adicionais e /receber/encomenda/indenizacoes).
 * 5. Prazo em dias úteis, contado a partir do primeiro dia útil seguinte ao da postagem.
 *
 * O que é ESTIMATIVA (documentado em docs/FRETE.md):
 * - O trecho oficial vem de tabelas internas dos Correios (Precificação Local/Capital/Divisa e matriz
 *   origem-destino), que não são públicas. Aqui ele é aproximado por cidade, estado, capitais e distância.
 * - Não existe tabela de balcão (à vista) pública em 2026. A tabela padrão abaixo usa a FORMA das tabelas
 *   oficiais Log+ 2024 e o NÍVEL de preço das consultas ao calculador oficial dos Correios em 14/10/2026.
 * - Prazos por trecho também são aproximados (os Correios não publicam tabela de prazos).
 */

export type CorreiosService = ShippingServiceKey;
export const ZONE_FAMILIES = ['L', 'E', 'N', 'P', 'I'] as const;
export type ZoneFamily = (typeof ZONE_FAMILIES)[number];
export type ZoneLevel = 1 | 2 | 3 | 4;
export type ZoneCode = `${ZoneFamily}${ZoneLevel}`;
export const ZONE_CODES: readonly ZoneCode[] = ZONE_FAMILIES.flatMap((f) => ([1, 2, 3, 4] as const).map((l) => `${f}${l}` as ZoneCode));

export const ZONE_FAMILY_INFO: Record<ZoneFamily, { label: string; description: string }> = {
  L: { label: 'Local', description: 'Mesma cidade da loja. No balcão dos Correios custa o mesmo que o nível E1.' },
  E: { label: 'Estadual', description: 'Outra cidade do mesmo estado, ou cidade de outro estado bem perto da divisa.' },
  N: { label: 'Entre capitais', description: 'Entre capitais e grandes centros (cidades até 90 km de uma capital) de estados diferentes.' },
  P: {
    label: 'Interior prioritário',
    description: 'Entre capitais e cidades "B" de outro estado. A lista é interna dos Correios, então a estimativa usa a coluna I.',
  },
  I: { label: 'Interestadual', description: 'Demais trechos entre estados diferentes.' },
};

export const SERVICE_LABELS: Record<CorreiosService, string> = { pac: 'PAC', sedex: 'SEDEX' };

// ---------------------------------------------------------------- Regras oficiais e parâmetros da estimativa

export const CORREIOS_RULES = {
  /** Fator volumétrico: peso cúbico (kg) = C × L × A (cm) ÷ 6.000. */
  cubicDivisor: 6000,
  /** Peso cúbico até 5 kg: cobra sempre o peso real. */
  cubicExemptUpToGrams: 5000,
  maxWeightGrams: 30000,
  maxSideCm: 100,
  maxSumCm: 200,
  /** Mínimo para pacote (Grandes Postagens/PLP): 11 × 6 × 0,4 cm. */
  minDimsCm: [11, 6, 0.4] as const,
  declaredValue: {
    percent: 2,
    automaticCoverageCents: 2563,
    maxCents: { pac: 466829, sedex: 3968048 } as Record<CorreiosService, number>,
  },
  /** Estimativa do trecho: limites em km (linha reta) dos níveis 1, 2 e 3; acima, nível 4. */
  levelBandsKm: {
    L: [Infinity, Infinity, Infinity],
    E: [150, 350, 600],
    N: [450, 700, 1100],
    P: [450, 700, 1100],
    I: [250, 600, 1100],
  } as Record<ZoneFamily, readonly [number, number, number]>,
  /** Cidade de outro estado a até 75 km (linha reta) conta como divisa (trecho E). */
  divisaKm: 75,
  /** Cidade a até 90 km (linha reta) da capital do próprio estado conta como grande centro (trecho N). */
  metroKm: 90,
} as const;

// ---------------------------------------------------------------- Tabela padrão

export interface ServicePriceTable {
  label: string;
  /** Limite superior de cada faixa de peso, em gramas. */
  brackets: readonly number[];
  /** Preço (centavos) por faixa, para cada coluna de trecho. */
  prices: Readonly<Partial<Record<ZoneCode, readonly number[]>>>;
  /** Centavos por kg (ou fração) acima da última faixa. */
  extraKgCents: Readonly<Partial<Record<ZoneCode, number>>>;
  /** Prazo base (dias úteis após a postagem) por família, nos níveis 1 a 4. */
  etaDays: Readonly<Record<ZoneFamily, readonly [number, number, number, number]>>;
}

export interface CorreiosPriceTable {
  id: string;
  label: string;
  validFrom: string;
  source: string;
  notes: readonly string[];
  services: Readonly<Record<CorreiosService, ServicePriceTable>>;
  /** Estados com logística remota (Amazônia): multiplicador de preço e dias a mais. */
  remote: { ufs: readonly UF[]; priceFactor: number; extraDays: Readonly<Record<CorreiosService, number>> };
}

const BRACKETS = [300, 500, 1000, 2000, 3000, 4000, 5000, 6000, 7000, 8000, 9000, 10000] as const;

/**
 * Tabela de referência "balcão 2026 (estimada)", em centavos, faixas de BRACKETS.
 * Derivação (passo a passo em docs/FRETE.md): preço = tabela oficial Correios Log+ 03/04/2024 (SEDEX "Infinite 1"
 * e PAC "Diamante 1") × multiplicador por faixa de peso, calibrado com 33 consultas ao calculador oficial dos
 * Correios (balcão, sem contrato) feitas em 14/10/2026:
 *   Estadual/local (E e L), multiplicador ancorado na coluna E1:
 *     PAC   0,3 kg R$ 23,30; 1 kg R$ 25,80; 2 kg R$ 29,70; 5 kg R$ 35,50; 10 kg R$ 48,50 (SP→SP, SP→Campinas,
 *           Goiânia→Goiânia, Goiânia→Anápolis).
 *     SEDEX 0,3 kg R$ 25,80; 1 kg R$ 27,60; 2 kg R$ 30,60; 5 kg R$ 39,10.
 *   Interestaduais (N, P e I), multiplicador quase constante: PAC 1,729 até 500 g e 1,802 acima; SEDEX 2,022.
 *     PAC   0,3 kg: SP→RJ R$ 27,50; Goiânia→Brasília R$ 26,90; Goiânia→SP R$ 34,20; Goiânia→Salvador R$ 41,00;
 *           Goiânia→Porto Alegre R$ 41,00; Goiânia→Uberlândia R$ 39,10. Goiânia→SP 1 kg R$ 38,00 e 5 kg R$ 57,00.
 *     SEDEX Goiânia→SP 0,3 kg R$ 77,10 e 1 kg R$ 83,60; Goiânia→Salvador R$ 89,80; Goiânia→Uberlândia R$ 86,60.
 *   Amazônia: Goiânia→Manaus PAC 0,3 kg R$ 49,30 (fator 1,207 sobre N4).
 * Resultado: todas as amostras reproduzidas com erro de até 1,2%. Colunas L do SEDEX = E1 (no balcão, a mesma
 * cidade custou o mesmo que outra cidade do estado). Faixas mais pesadas nunca ficam mais baratas que a anterior.
 */
export const CORREIOS_DEFAULT_TABLE: CorreiosPriceTable = {
  id: 'balcao-2026-estimada',
  label: 'Balcão dos Correios 2026 (estimada)',
  validFrom: '2026-04-12',
  source:
    'Forma: tabelas oficiais Correios Log+ (SEDEX Infinite 1 e PAC Diamante 1, vigência 03/04/2024). Nível: calculador oficial de preços e prazos dos Correios, consultas de 14/10/2026.',
  notes: [
    'Valores de referência para quem posta no balcão, sem contrato. Com contrato ou plataforma de frete, use o ajuste percentual (ex.: -30%).',
    'O trecho (L, E, N, I) é estimado pela cidade, estado e distância; a matriz oficial dos Correios não é pública.',
    'A coluna P (interior prioritário) existe na tabela oficial, mas a lista de cidades não é pública: a estimativa usa I.',
  ],
  services: {
    pac: {
      label: 'PAC',
      brackets: BRACKETS,
      prices: {
        E1: [2330, 2330, 2580, 2970, 3281, 3409, 3550, 3862, 4196, 4516, 4711, 4850],
        E2: [2431, 2431, 2689, 3097, 3419, 3556, 3700, 4025, 4373, 4709, 4912, 5055],
        E3: [2453, 2453, 2717, 3127, 3454, 3592, 3738, 4067, 4422, 4757, 4962, 5104],
        E4: [2480, 2480, 2746, 3161, 3491, 3626, 3775, 4108, 4464, 4806, 5012, 5158],
        N1: [2722, 2722, 3039, 3340, 3992, 4266, 4560, 5048, 5575, 6072, 6371, 6584],
        N2: [3049, 3049, 3403, 3740, 4472, 4776, 5106, 5805, 6411, 6982, 7328, 7574],
        N3: [3402, 3402, 3799, 4174, 4989, 5331, 5701, 6626, 7316, 7968, 8363, 8644],
        N4: [4083, 4083, 4562, 5011, 5989, 6398, 6838, 8204, 9059, 9866, 10355, 10701],
        P1: [2802, 2802, 3096, 3673, 4234, 5429, 5681, 6584, 7036, 9066, 9325, 9508],
        P2: [3390, 3390, 3727, 4338, 4966, 6191, 6474, 7557, 8076, 10171, 10465, 10679],
        P3: [5688, 5688, 6151, 6793, 7496, 8745, 9066, 10346, 10939, 13103, 13438, 13680],
        P4: [7811, 7811, 8404, 9113, 9956, 11265, 11647, 13303, 14037, 16337, 16755, 17054],
        I1: [3256, 3256, 3599, 4272, 4924, 6314, 6607, 7656, 8183, 10542, 10843, 11056],
        I2: [3941, 3941, 4335, 5045, 5773, 7197, 7527, 8787, 9390, 11827, 12170, 12417],
        I3: [6615, 6615, 7152, 7900, 8716, 10169, 10542, 12029, 12719, 15235, 15627, 15908],
        I4: [9082, 9082, 9774, 10598, 11577, 13100, 13545, 15468, 16322, 18997, 19480, 19830],
      },
      extraKgCents: {
        E1: 601, E2: 626, E3: 632, E4: 641, N1: 816, N2: 939, N3: 1072, N4: 1328,
        P1: 1179, P2: 1325, P3: 1698, P4: 2114, I1: 1372, I2: 1541, I3: 1974, I4: 2457,
      },
      // Amostras: mesma cidade, mesmo estado e SP→RJ +5; Goiânia→SP +6; Goiânia→Uberlândia +7; Goiânia→Porto Alegre +7.
      etaDays: { L: [5, 5, 5, 5], E: [5, 6, 6, 7], N: [5, 6, 6, 7], P: [6, 7, 8, 9], I: [6, 7, 8, 9] },
    },
    sedex: {
      label: 'SEDEX',
      brackets: BRACKETS,
      prices: {
        L1: [2580, 2623, 2760, 3060, 3329, 3640, 3910, 4316, 4724, 5161, 5613, 6112],
        L2: [2580, 2623, 2760, 3060, 3329, 3640, 3910, 4316, 4724, 5161, 5613, 6112],
        L3: [2580, 2623, 2760, 3060, 3329, 3640, 3910, 4316, 4724, 5161, 5613, 6112],
        L4: [2580, 2623, 2760, 3060, 3329, 3640, 3910, 4316, 4724, 5161, 5613, 6112],
        E1: [2580, 2623, 2760, 3060, 3329, 3640, 3910, 4316, 4724, 5161, 5613, 6112],
        E2: [2637, 2679, 2791, 3093, 3361, 3679, 3951, 4361, 4774, 5216, 5668, 6176],
        E3: [2693, 2737, 2820, 3124, 3397, 3716, 3991, 4405, 4821, 5268, 5727, 6237],
        E4: [2865, 2913, 2913, 3156, 3430, 3754, 4032, 4450, 4869, 5321, 5785, 6303],
        N1: [4279, 4459, 4639, 5594, 6530, 7488, 8265, 9060, 9998, 10951, 11905, 12864],
        N2: [5994, 6245, 6498, 7832, 8817, 10109, 11157, 12229, 13497, 14787, 16071, 17363],
        N3: [7705, 8026, 8352, 10069, 12407, 14227, 15701, 17212, 18995, 20813, 22621, 24439],
        N4: [8989, 9365, 9745, 11747, 15019, 17222, 19005, 20835, 22993, 25191, 27384, 29586],
        P1: [5699, 5850, 6002, 7503, 8991, 9794, 11848, 12514, 13307, 14809, 15608, 16411],
        P2: [7280, 7490, 7701, 9525, 11048, 12138, 14417, 15321, 16384, 18166, 19254, 20334],
        P3: [8787, 9058, 9329, 11472, 14136, 15665, 18304, 19576, 21072, 23299, 24821, 26346],
        P4: [10077, 10390, 10706, 13094, 16544, 18395, 21292, 22831, 24641, 27189, 29030, 30878],
        I1: [6785, 6965, 7145, 8930, 10704, 11660, 14105, 14898, 15840, 17630, 18581, 19539],
        I2: [8665, 8916, 9167, 11339, 13153, 14451, 17163, 18237, 19505, 21626, 22920, 24209],
        I3: [10459, 10781, 11106, 13656, 16829, 18647, 21790, 23303, 25086, 27735, 29549, 31365],
        I4: [11996, 12370, 12744, 15588, 19695, 21897, 25349, 27181, 29337, 32368, 34561, 36761],
      },
      extraKgCents: {
        L1: 758, L2: 758, L3: 758, L4: 758, E1: 758, E2: 766, E3: 773, E4: 781, N1: 1596, N2: 2154, N3: 3029, N4: 3670,
        P1: 2034, P2: 2520, P3: 3268, P4: 3828, I1: 2423, I2: 3001, I3: 3891, I4: 4558,
      },
      // Amostras: Goiânia→SP +2 e Goiânia→Salvador +2 (N); Goiânia→Uberlândia +3 (I2).
      etaDays: { L: [1, 1, 1, 1], E: [1, 2, 2, 3], N: [1, 2, 2, 3], P: [2, 3, 3, 4], I: [2, 3, 4, 5] },
    },
  },
  // Goiânia→Manaus (PAC 0,3 kg) custou 1,207 × N4 e levou 18 dias úteis (N4 = 7).
  remote: { ufs: ['AM', 'RR', 'AP', 'AC'], priceFactor: 1.207, extraDays: { pac: 11, sedex: 3 } },
};

// ---------------------------------------------------------------- Formatação

const nf1 = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });
const nf2 = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 });
const nf0 = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });

export const formatKm = (km: number): string => `${km >= 100 ? nf0.format(km) : nf1.format(km)} km`;
export const formatKg = (grams: number): string => `${nf2.format(grams / 1000)} kg`;
export const formatCm = (cm: number): string => nf1.format(cm);

// ---------------------------------------------------------------- Volumes (embalagem)

export interface ShippingItemInput {
  quantity: number;
  weightGrams?: number | null;
  lengthCm?: number | null;
  widthCm?: number | null;
  heightCm?: number | null;
  /** Valor unitário (centavos) para o valor declarado. */
  unitValueCents?: number | null;
}

export interface PackageDefaults {
  weightGrams: number;
  lengthCm: number;
  widthCm: number;
  heightCm: number;
}

export interface PackedBox {
  /** Medidas em ordem decrescente (comprimento ≥ largura ≥ altura), cm. */
  lengthCm: number;
  widthCm: number;
  heightCm: number;
  realWeightG: number;
  cubicWeightG: number;
  billedWeightG: number;
  units: number;
  valueCents: number;
}

export interface PackResult {
  boxes: PackedBox[];
  realWeightG: number;
  cubicWeightG: number;
  billedWeightG: number;
  units: number;
  /** Unidades que usaram a embalagem padrão (produto sem peso ou sem medidas). */
  unitsWithDefault: number;
  /** Algum produto passa dos limites dos Correios (a opção fica indisponível). */
  error: string | null;
}

/** Peso cúbico em gramas: C × L × A ÷ 6.000 (kg) = C × L × A ÷ 6 (g). */
export function cubicWeightGrams(lengthCm: number, widthCm: number, heightCm: number): number {
  return Math.ceil((lengthCm * widthCm * heightCm * 1000) / CORREIOS_RULES.cubicDivisor);
}

/** Peso cobrado: o cúbico só vale quando passa de 5 kg; aí cobra o maior dos dois. */
export function billedWeightGrams(realG: number, cubicG: number): number {
  return cubicG > CORREIOS_RULES.cubicExemptUpToGrams ? Math.max(realG, cubicG) : realG;
}

const pos = (v: number | null | undefined): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0;

interface UnitSpec {
  dims: [number, number, number];
  weightG: number;
  valueCents: number;
  count: number;
}

/**
 * Junta os itens em volumes. Heurística (documentada): cada unidade vira uma caixa com as medidas do produto
 * (ou a embalagem padrão), deitada com a menor medida como altura; as unidades são empilhadas (altura soma,
 * comprimento e largura ficam no maior) até bater 30 kg, 100 cm de altura ou 200 cm somando os 3 lados.
 * Aí abre um novo volume. Cada volume é cobrado separado, como nos Correios.
 */
export function packItems(items: readonly ShippingItemInput[], defaults: PackageDefaults): PackResult {
  const R = CORREIOS_RULES;
  const specs: UnitSpec[] = [];
  let unitsWithDefault = 0;
  let units = 0;
  for (const it of items) {
    const qty = Number(it.quantity);
    if (!Number.isFinite(qty) || qty <= 0) continue;
    const count = Math.max(1, Math.ceil(qty - 1e-9));
    const hasDims = pos(it.lengthCm) && pos(it.widthCm) && pos(it.heightCm);
    const hasWeight = pos(it.weightGrams);
    if (!hasDims || !hasWeight) unitsWithDefault += count;
    const dims = (hasDims ? [it.lengthCm!, it.widthCm!, it.heightCm!] : [defaults.lengthCm, defaults.widthCm, defaults.heightCm])
      .map((d) => Math.max(0.1, d))
      .sort((a, b) => b - a) as [number, number, number];
    // Produto fracionado (kg, metro...): o peso total é quantidade × peso da unidade.
    const weightG = Math.max(1, Math.round(((hasWeight ? it.weightGrams! : defaults.weightGrams) * qty) / count));
    const valueCents = Math.max(0, Math.round(((it.unitValueCents ?? 0) * qty) / count));
    if (weightG > R.maxWeightGrams) {
      return emptyPack(`Um dos produtos pesa mais de ${formatKg(R.maxWeightGrams)}, o limite dos Correios por volume.`);
    }
    if (dims[0] > R.maxSideCm || dims[0] + dims[1] + dims[2] > R.maxSumCm) {
      return emptyPack(
        `Um dos produtos passa das medidas máximas dos Correios (${R.maxSideCm} cm por lado e ${R.maxSumCm} cm somando os 3 lados).`,
      );
    }
    specs.push({ dims, weightG, valueCents, count });
    units += count;
  }
  if (!specs.length) return emptyPack(null);

  // Maiores primeiro (pela base), para o comprimento e a largura do volume serem definidos logo.
  specs.sort((a, b) => b.dims[0] * b.dims[1] - a.dims[0] * a.dims[1]);
  const boxes: PackedBox[] = [];
  let cur: { l: number; w: number; h: number; weight: number; units: number; value: number } | null = null;
  const close = () => {
    if (!cur) return;
    const dims = [cur.l, cur.w, cur.h].map((d) => Math.round(d * 10) / 10).sort((a, b) => b - a) as [number, number, number];
    const cubic = cubicWeightGrams(dims[0], dims[1], dims[2]);
    boxes.push({
      lengthCm: dims[0],
      widthCm: dims[1],
      heightCm: dims[2],
      realWeightG: cur.weight,
      cubicWeightG: cubic,
      billedWeightG: billedWeightGrams(cur.weight, cubic),
      units: cur.units,
      valueCents: cur.value,
    });
    cur = null;
  };
  for (const s of specs) {
    let left = s.count;
    while (left > 0) {
      if (!cur) cur = { l: s.dims[0], w: s.dims[1], h: 0, weight: 0, units: 0, value: 0 };
      const l = Math.max(cur.l, s.dims[0]);
      const w = Math.max(cur.w, s.dims[1]);
      const byWeight = Math.floor((R.maxWeightGrams - cur.weight) / s.weightG);
      const maxH = Math.min(R.maxSideCm, R.maxSumCm - l - w);
      const byHeight = Math.floor((maxH - cur.h + 1e-9) / s.dims[2]);
      const fit = Math.min(left, byWeight, byHeight);
      if (fit <= 0) {
        if (cur.units === 0) {
          // Nem uma unidade cabe sozinha (só acontece em medidas no limite): volume com 1 unidade.
          cur = { l, w, h: s.dims[2], weight: s.weightG, units: 1, value: s.valueCents };
          left -= 1;
        }
        close();
        continue;
      }
      cur.l = l;
      cur.w = w;
      cur.h += fit * s.dims[2];
      cur.weight += fit * s.weightG;
      cur.units += fit;
      cur.value += fit * s.valueCents;
      left -= fit;
    }
  }
  close();
  return {
    boxes,
    realWeightG: boxes.reduce((a, b) => a + b.realWeightG, 0),
    cubicWeightG: boxes.reduce((a, b) => a + b.cubicWeightG, 0),
    billedWeightG: boxes.reduce((a, b) => a + b.billedWeightG, 0),
    units,
    unitsWithDefault,
    error: null,
  };
}

function emptyPack(error: string | null): PackResult {
  return { boxes: [], realWeightG: 0, cubicWeightG: 0, billedWeightG: 0, units: 0, unitsWithDefault: 0, error };
}

// ---------------------------------------------------------------- Trecho (zona)

export interface ZonePlace extends PlaceRef {
  lat?: number | null;
  lng?: number | null;
  precision?: GeoPrecision;
}

export type ZoneBasis = 'same_city' | 'same_state' | 'divisa' | 'capitals' | 'interstate';

export interface ZoneResult {
  family: ZoneFamily;
  level: ZoneLevel;
  code: ZoneCode;
  basis: ZoneBasis;
  /** Distância em linha reta entre origem e destino (null sem coordenadas). */
  straightKm: number | null;
  /** Origem ou destino em estado de logística remota (Amazônia). */
  remote: boolean;
  /** Sempre true: a matriz oficial não é pública. Mais impreciso quando a coordenada é só da cidade/estado. */
  approximate: true;
  label: string;
  explanation: string;
}

const coords = (p: ZonePlace): LatLng | null => (isValidLatLng(p.lat, p.lng) ? { lat: p.lat!, lng: p.lng! } : null);

/** Cidade a até `metroKm` da capital do próprio estado (ou a própria capital): proxy de "grande centro" (A+). */
function isBigCenter(p: ZonePlace, uf: UF): boolean {
  if (isCapitalCity(p)) return true;
  const c = coords(p);
  if (!c || p.precision === 'state' || p.precision === 'none') return false;
  return haversineKm(c, capitalOf(uf)) <= CORREIOS_RULES.metroKm;
}

function levelFor(family: ZoneFamily, km: number | null): ZoneLevel {
  if (km == null) return family === 'E' ? 2 : 3;
  const bands = CORREIOS_RULES.levelBandsKm[family];
  if (km <= bands[0]) return 1;
  if (km <= bands[1]) return 2;
  if (km <= bands[2]) return 3;
  return 4;
}

const placeName = (p: ZonePlace, uf: UF) => `${p.city?.trim() || UF_INFO[uf].name} (${uf})`;

/**
 * Estima o trecho dos Correios entre a loja e o destino.
 * Regra (aproximação documentada): mesma cidade = L1; mesmo estado = E; outro estado a até 75 km = E (divisa);
 * capitais/grandes centros dos dois lados = N; demais = I. Nível 1 a 4 pela distância em linha reta.
 * Retorna null quando não dá para saber o estado de um dos lados.
 */
export function classifyZone(origin: ZonePlace, dest: ZonePlace, table: CorreiosPriceTable = CORREIOS_DEFAULT_TABLE): ZoneResult | null {
  const ou = toUF(origin.uf);
  const du = toUF(dest.uf);
  if (!ou || !du) return null;
  const oc = coords(origin);
  const dc = coords(dest);
  let km: number | null = oc && dc ? roundKm(haversineKm(oc, dc)) : null;
  const remote = table.remote.ufs.includes(ou) || table.remote.ufs.includes(du);
  const from = placeName(origin, ou);
  const to = placeName(dest, du);
  const kmText = (k: number | null) => (k == null ? 'distância desconhecida' : `${formatKm(k)} em linha reta`);

  let family: ZoneFamily;
  let basis: ZoneBasis;
  let why: string;
  if (isSameCity(origin, dest)) {
    family = 'L';
    basis = 'same_city';
    why = `${from} para ${to}: mesma cidade.`;
  } else if (ou === du) {
    family = 'E';
    basis = 'same_state';
    why = `${from} para ${to}: mesmo estado, ${kmText(km)}.`;
  } else if (km != null && km <= CORREIOS_RULES.divisaKm) {
    family = 'E';
    basis = 'divisa';
    why = `${from} para ${to}: outro estado, mas a ${formatKm(km)} (cidade de divisa).`;
  } else {
    const big = isBigCenter(origin, ou) && isBigCenter(dest, du);
    family = big ? 'N' : 'I';
    basis = big ? 'capitals' : 'interstate';
    // Sem coordenadas, mede entre as capitais dos dois estados.
    if (km == null) km = roundKm(haversineKm(capitalOf(ou), capitalOf(du)));
    why = big
      ? `${from} para ${to}: estados diferentes, as duas são capitais ou grandes centros, ${kmText(km)}.`
      : `${from} para ${to}: estados diferentes, ${kmText(km)}.`;
  }
  const level: ZoneLevel = family === 'L' ? 1 : levelFor(family, km);
  const code = `${family}${level}` as ZoneCode;
  const info = ZONE_FAMILY_INFO[family];
  const approxCoords = [origin.precision, dest.precision].some((p) => p && p !== 'street');
  return {
    family,
    level,
    code,
    basis,
    straightKm: km,
    remote,
    approximate: true,
    label: `${info.label}, nível ${level} (${code})`,
    explanation:
      why +
      (remote ? ' Trecho com estado da Amazônia: preço e prazo maiores.' : '') +
      (approxCoords ? ' Localização aproximada pelo centro da cidade.' : ''),
  };
}

// ---------------------------------------------------------------- Preço

/** Coluna efetiva: o PAC não tem colunas L (usa E1). Sem a coluna na tabela, cai para E1. */
export function effectiveColumn(table: ServicePriceTable, code: ZoneCode): ZoneCode {
  if (table.prices[code]) return code;
  return 'E1';
}

/** Preço de tabela de um volume (faixa de peso + kg adicional acima da última faixa). */
export function tablePriceCents(table: ServicePriceTable, code: ZoneCode, billedWeightG: number): number {
  const col = effectiveColumn(table, code);
  const prices = table.prices[col]!;
  const idx = table.brackets.findIndex((max) => billedWeightG <= max);
  if (idx >= 0) return prices[idx]!;
  const last = table.brackets[table.brackets.length - 1]!;
  const extraKg = Math.ceil((billedWeightG - last) / 1000);
  return prices[prices.length - 1]! + extraKg * (table.extraKgCents[col] ?? 0);
}

/** Valor declarado de um volume: 2% sobre o que passar da indenização automática, com teto por serviço. */
export function declaredValueFeeCents(service: CorreiosService, valueCents: number): { declaredCents: number; feeCents: number } {
  const dv = CORREIOS_RULES.declaredValue;
  const declared = Math.min(Math.max(0, Math.round(valueCents)), dv.maxCents[service]);
  const fee = Math.round((Math.max(0, declared - dv.automaticCoverageCents) * dv.percent) / 100);
  return { declaredCents: declared, feeCents: fee };
}

export interface CorreiosQuote {
  service: CorreiosService;
  available: boolean;
  reason: string | null;
  /** null = não foi possível estimar (fica a combinar). */
  priceCents: number | null;
  tableCents: number | null;
  zone: ZoneResult | null;
  adjustments: { label: string; cents: number }[];
  declaredValueCents: number;
  eta: { min: number; max: number; baseDays: number } | null;
  notes: string[];
}

export interface CorreiosQuoteInput {
  service: CorreiosService;
  origin: ZonePlace | null;
  destination: ZonePlace | null;
  pack: PackResult;
  settings: Pick<CalculatedShippingSettings, 'adjustPercent' | 'handlingFeeCents' | 'extraDays' | 'declaredValue'>;
  table?: CorreiosPriceTable;
}

export function etaText(eta: { min: number; max: number } | null): string {
  if (!eta) return '';
  if (eta.min === eta.max) return eta.max === 1 ? '1 dia útil' : `${eta.max} dias úteis`;
  return `de ${eta.min} a ${eta.max} dias úteis`;
}

/** Cálculo completo de um serviço (PAC ou SEDEX) no método dos Correios. */
export function quoteCorreios(input: CorreiosQuoteInput): CorreiosQuote {
  const table = input.table ?? CORREIOS_DEFAULT_TABLE;
  const svc = table.services[input.service];
  const base: CorreiosQuote = {
    service: input.service,
    available: true,
    reason: null,
    priceCents: null,
    tableCents: null,
    zone: null,
    adjustments: [],
    declaredValueCents: 0,
    eta: null,
    notes: [],
  };
  if (input.pack.error) return { ...base, available: false, reason: input.pack.error };
  if (!input.pack.boxes.length) return { ...base, reason: 'Carrinho sem produtos para calcular o frete.' };
  if (!input.origin) return { ...base, reason: 'A loja ainda não informou o CEP de origem. O frete será combinado.' };
  if (!input.destination) return { ...base, reason: 'Não foi possível localizar o CEP agora. O frete será combinado.' };
  const zone = classifyZone(input.origin, input.destination, table);
  if (!zone) return { ...base, reason: 'Não foi possível identificar o estado do CEP. O frete será combinado.' };

  const factor = zone.remote ? table.remote.priceFactor : 1;
  let tableCents = 0;
  let declared = 0;
  let vdFee = 0;
  for (const box of input.pack.boxes) {
    tableCents += Math.round(tablePriceCents(svc, zone.code, box.billedWeightG) * factor);
    if (input.settings.declaredValue) {
      const dv = declaredValueFeeCents(input.service, box.valueCents);
      declared += dv.declaredCents;
      vdFee += dv.feeCents;
    }
  }
  const adjustments: { label: string; cents: number }[] = [];
  const pct = input.settings.adjustPercent;
  if (pct) {
    const cents = Math.round((tableCents * pct) / 100);
    adjustments.push({ label: `Ajuste da loja na tabela (${pct > 0 ? '+' : ''}${nf1.format(pct)}%)`, cents });
  }
  if (input.settings.declaredValue) {
    adjustments.push({ label: `Valor declarado (seguro) de ${formatMoney(declared)}`, cents: vdFee });
  }
  if (input.settings.handlingFeeCents > 0) {
    adjustments.push({ label: 'Embalagem e manuseio', cents: input.settings.handlingFeeCents });
  }
  const priceCents = Math.max(0, tableCents + adjustments.reduce((a, b) => a + b.cents, 0));

  const days = svc.etaDays[zone.family][zone.level - 1]! + (zone.remote ? table.remote.extraDays[input.service] : 0);
  const extra = Math.max(0, input.settings.extraDays);
  const eta = { min: Math.max(1, days - 1) + extra, max: days + extra, baseDays: days };

  const p = input.pack;
  const col = effectiveColumn(svc, zone.code);
  const notes = [
    `Trecho ${zone.label}${col !== zone.code ? `; o ${svc.label} não tem essa coluna e usa a ${col}` : ''}. ${zone.explanation}`,
    `Peso real ${formatKg(p.realWeightG)}; peso cúbico ${formatKg(p.cubicWeightG)} (C × L × A ÷ 6.000, só conta acima de 5 kg por volume); peso cobrado ${formatKg(p.billedWeightG)}.`,
  ];
  if (p.boxes.length > 1) notes.push(`${p.boxes.length} volumes: cada um passa de 30 kg ou das medidas máximas se ficasse junto.`);
  if (p.unitsWithDefault > 0) notes.push(`${p.unitsWithDefault} unidade(s) sem peso ou medidas no cadastro: usamos a embalagem padrão da loja.`);
  notes.push(`Tabela de referência do ${svc.label}: ${formatMoney(tableCents)}${zone.remote ? ', com acréscimo de logística remota' : ''}.`);
  for (const a of adjustments) notes.push(`${a.label}: ${a.cents < 0 ? '-' : '+'}${formatMoney(Math.abs(a.cents))}.`);
  notes.push(
    `Prazo: ${days} ${days === 1 ? 'dia útil' : 'dias úteis'} dos Correios após a postagem${extra ? ` + ${extra} ${extra === 1 ? 'dia' : 'dias'} para a loja postar` : ''}.`,
  );
  return { ...base, priceCents, tableCents, zone, adjustments, declaredValueCents: declared, eta, notes };
}
